export interface SpecificationValidationResult {
  isValid: boolean;
  normalized: string;
  error?: string;
}

const MAX_SPECIFICATION_LENGTH = 30;
const NUMBER = String.raw`\d+(?:\.\d+)?`;
const UNIT = String.raw`(?:ng|µg|mg|g|kg|ml|l|IU|U|%)`;
const QUANTITY = String.raw`${NUMBER}${UNIT}`;
const DENOMINATOR = String.raw`(?:${QUANTITY}|ml|l|g|片|粒|支|瓶|袋|贴|揿|吸|喷)`;
const SPECIFICATION_PATTERN = new RegExp(
  String.raw`^${QUANTITY}(?:(?:\/|\+)${DENOMINATOR}){0,3}$`,
);

function normalizeUnits(value: string): string {
  return value
    .replace(/μ/g, "µ")
    .replace(/mcg|ug/gi, "µg")
    .replace(/ng/gi, "ng")
    .replace(/mg/gi, "mg")
    .replace(/kg/gi, "kg")
    .replace(/ml/gi, "ml")
    .replace(/iu/gi, "IU")
    .replace(/(?<![a-z])u(?![a-z])/gi, "U")
    .replace(/(?<![a-z])g(?![a-z])/gi, "g")
    .replace(/(?<![a-z])l(?![a-z])/gi, "l");
}

export function normalizeSpecification(value: string): string {
  return normalizeUnits(
    value
      .trim()
      .replace(/\s+/g, "")
      .replace(/[＋﹢]/g, "+")
      .replace(/[／]/g, "/"),
  );
}

export function validateSpecification(value: string): SpecificationValidationResult {
  const normalized = normalizeSpecification(value);

  if (!normalized) {
    return { isValid: true, normalized: "" };
  }

  if (normalized.length > MAX_SPECIFICATION_LENGTH) {
    return {
      isValid: false,
      normalized,
      error: `规格不能超过 ${MAX_SPECIFICATION_LENGTH} 个字符`,
    };
  }

  if (/^\d+(?:\.\d+)?$/.test(normalized)) {
    return {
      isValid: false,
      normalized,
      error: "请在数值后填写单位，例如 20mg",
    };
  }

  if (!SPECIFICATION_PATTERN.test(normalized)) {
    return {
      isValid: false,
      normalized,
      error: "格式不正确，可输入 20mg、100mg/5ml、24mg/26mg、50mg+500mg 或 0.1%",
    };
  }

  return { isValid: true, normalized };
}

export function getSpecificationPlaceholder(dosageForm: string): string {
  if (["syrup", "oral_solution", "suspension", "emulsion"].includes(dosageForm)) {
    return "例如：100mg/5ml";
  }

  if (["injection", "lyophilized_powder", "infusion", "nebulizer"].includes(dosageForm)) {
    return "例如：10mg/ml 或 100mg/2ml";
  }

  if (["ointment", "cream", "gel", "eye_drops", "ear_drops"].includes(dosageForm)) {
    return "例如：0.1% 或 10mg/g";
  }

  return "例如：20mg 或 24mg/26mg";
}
